<script setup lang="ts">
import { observeElementIntersection } from '@@/bits/intersection'
import { requestIdleCallback } from '@@/bits/request_idle_callback'
import { onMounted, ref, computed } from 'vue'
import { useWindowSizeStore } from '@@/pinia/window_size'

const props = withDefaults(
  defineProps<{
    tag?: string
    minHeight: number
    minWidth: number
    renderImmediately?: boolean
    rootMargin?: number
  }>(),
  {
    tag: 'div',
    renderImmediately: false,
    rootMargin: undefined,
  },
)

const isVisible = ref(props.renderImmediately)
const rootElement = ref<HTMLElement | null>(null)
// Set the default root margin to 50% of window height so that the lazy loading is triggered when the element is 50% of the screen's height.
const defaultRootMargin = computed(() => useWindowSizeStore().windowHeight / 2)

onMounted(() => {
  if (isVisible.value) return
  requestIdleCallback(() => {
    if (rootElement.value === null) return
    observeElementIntersection(
      rootElement.value,
      () => {
        requestIdleCallback(() => {
          isVisible.value = true
        })
      },
      {
        // rootMargin creates a virtual boundary around the viewport for intersection detection
        rootMargin: `${props.rootMargin ?? defaultRootMargin.value}px`,
        // Use wall-container as the root element for SurfaceLazy to intersect with.
        root: document.getElementById('wall-container'),
      },
    )
  })
})
</script>

<template>
  <component
    :is="tag"
    ref="rootElement"
    :style="[
      // Before the children is rendered, we need to set the minHeight and minWidth
      // in order for the intersectionObserver to take into consideration the minimum size of the children
      !isVisible && {
        minHeight: `${minHeight}px`,
        minWidth: `${minWidth}px`,
      },
    ]"
  >
    <slot v-if="isVisible"></slot>
  </component>
</template>
